// ✅ src/scenes/global/Sidebar.jsx

import { useState, useEffect } from "react";
import { ProSidebar, Menu, MenuItem } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { Box, IconButton, Typography, useTheme, Avatar } from "@mui/material";
import { Link } from "react-router-dom";
import { tokens } from "../../theme/theme";
import HouseOutlinedIcon from "@mui/icons-material/HouseOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline"; // ✅ NEW

// ✅ Centralized backend API base URL
const BASE_URL = process.env.REACT_APP_BASE_URL ?? "";

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const textColor = theme.palette.mode === "light" ? "#ffffff" : colors.grey[100];

  return (
    <MenuItem
      active={selected === title}
      style={{ color: textColor }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selected, setSelected] = useState("Dashboard");
  const [userInfo, setUserInfo] = useState(null);
  const textColor = theme.palette.mode === "light" ? "#ffffff" : colors.grey[100];
  const subHeaderColor = theme.palette.mode === "light" ? "#dddddd" : colors.grey[300];

  useEffect(() => {
    async function fetchProfile() {
      try {
        const res = await fetch(`${BASE_URL}/auth/me`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setUserInfo(data);
        }
      } catch (err) {
        console.error("Failed to fetch profile info:", err);
      }
    }

    fetchProfile();
  }, []);

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: colors.sidebar.main,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .pro-item-content:hover": {
          color: "#11fa47 !important",
        },
        "& .pro-menu-item.active": {
          color: "#11fa47 !important",
        },
        color: textColor,
      }}
    >
      <ProSidebar collapsed={isCollapsed}>
        <Menu iconShape="square">
          {/* TOGGLE */}
          <MenuItem
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{ margin: "10px 0 20px 0", color: textColor }}
          >
            {!isCollapsed ? (
              <Box display="flex" justifyContent="space-between" alignItems="center" ml="15px">
                <Typography variant="h3" color={textColor}>
                  SPICIERWORKS
                </Typography>
                <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                  <MenuOutlinedIcon sx={{ color: "#fff" }} />
                </IconButton>
              </Box>
            ) : (
              <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
                <MenuOutlinedIcon sx={{ color: "#fff" }} />
              </IconButton>
            )}
          </MenuItem>

          {!isCollapsed && (
            <Box mb="25px">
              <Box display="flex" justifyContent="center" alignItems="center">
                {userInfo?.photo ? (
                  <Avatar
                    src={userInfo.photo}
                    alt="profile-user"
                    sx={{ width: 100, height: 100 }}
                  />
                ) : (
                  <Avatar sx={{ width: 100, height: 100 }}>
                    {userInfo?.name?.charAt(0) || "K"}
                  </Avatar>
                )}
              </Box>
              <Box textAlign="center" mt={1}>
                <Typography variant="h2" color={textColor} fontWeight="bold">
                  {userInfo?.name || "Keith Schneider"}
                </Typography>
              </Box>
            </Box>
          )}

          <Box paddingLeft={isCollapsed ? undefined : "10%"}>
            <Item
              title="Dashboard"
              to="/"
              icon={<HouseOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Typography variant="h6" color={subHeaderColor} sx={{ m: "15px 0 5px 20px" }}>
              Pages
            </Typography>
            <Item
              title="Triage"
              to="/triage"
              icon={<CalendarMonthOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Completed Tickets"
              to="/complete"
              icon={<AssignmentTurnedInOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="News Releases & Notices"
              to="/notices"
              icon={<CampaignOutlinedIcon />}
              selected={selected}
              setSelected={setSelected}
            />
            <Item
              title="Help"
              to="/help"
              icon={<HelpOutlineIcon />}
              selected={selected}
              setSelected={setSelected}
            />
          </Box>
        </Menu>
      </ProSidebar>
    </Box>
  );
};

export default Sidebar;